import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  ediFieldMappingService,
  FieldMappingSaveError,
  type EdiFieldMappingScope,
  type ResolveFieldMappingResponse,
  type ListFieldMappingEntriesResponse,
  type SaveFieldMappingRequest,
  type SaveFieldMappingResponse,
} from '../../services/api/ediFieldMappingService';

// ============================================================================
// EDI Field Mapping thunks — Spec mlinkone-buyer-po-field-mapping (group 8).
// ============================================================================

/** Resolve the effective per-property map for a scope. */
export const resolveFieldMapping = createAsyncThunk<
  ResolveFieldMappingResponse,
  EdiFieldMappingScope
>(
  'ediFieldMapping/resolve',
  async (scope, { rejectWithValue }) => {
    try {
      return await ediFieldMappingService.resolve(scope);
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to resolve field mapping');
    }
  }
);

/** Load the raw override entries authored at a scope (for the editor). */
export const listFieldMappingEntries = createAsyncThunk<
  ListFieldMappingEntriesResponse,
  EdiFieldMappingScope
>(
  'ediFieldMapping/listEntries',
  async (scope, { rejectWithValue }) => {
    try {
      return await ediFieldMappingService.listEntries(scope);
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to list field-mapping entries');
    }
  }
);

/**
 * Save (create/supersede) the override entries at a scope. On BE validation
 * failure the rejected payload is the `messages[]` array so the page can
 * render each server-side error verbatim.
 */
export const saveFieldMapping = createAsyncThunk<
  SaveFieldMappingResponse,
  SaveFieldMappingRequest,
  { rejectValue: string[] }
>(
  'ediFieldMapping/save',
  async (request, { rejectWithValue }) => {
    try {
      return await ediFieldMappingService.save(request);
    } catch (error: unknown) {
      if (error instanceof FieldMappingSaveError) {
        return rejectWithValue(error.messages);
      }
      return rejectWithValue([error instanceof Error ? error.message : 'Failed to save field mapping']);
    }
  }
);
